// @ts-check

/**
 * @returns {boolean}
 */
export const isDefaultLanguage = () => process.env.GATSBY_LANGUAGE === "en-US";

/**
 * @returns {boolean}
 */
export const isStoryblokPreview = () =>
	process.env.GATSBY_STORYBLOK_STATUS === "draft";

/**
 * @private
 * @returns {string}
 */
const getStoryBlokTokenName = () =>
	isStoryblokPreview() ? "STORYBLOK_CDN_PREVIEW_TOKEN" : "STORYBLOK_CDN_TOKEN";

/**
 * This array contains the names of all required environment variables.
 * If any of these are missing, the build will fail.
 *
 * @returns {string[]}
 */
export const requiredEnvironmentVariables = () => [
	"COSMOS_DB_CONNECTION_STRING",
	"GATSBY_LANGUAGE",
	"GATSBY_STORE",
	"COMMERCETOOLS_PROJECT_KEY",
	"COMMERCETOOLS_API_URL",
	"COMMERCETOOLS_AUTH_URL",
	"COMMERCETOOLS_FRONTEND_CLIENT_ID",
	"COMMERCETOOLS_FRONTEND_SCOPES",
	"COMMERCETOOLS_FRONTEND_CLIENT_SECRET",
	"COMMERCETOOLS_GATSBY_FUNCTIONS_CLIENT_SECRET",
	"COMMERCETOOLS_GATSBY_FUNCTIONS_CLIENT_ID",
	"BAZAARVOICE_ENV",
	"MAPBOX_PUBLIC_ACCESS_TOKEN",
	"PO_EDITOR_PROJECT_ID_V2",
	"PO_EDITOR_API_TOKEN_V2",
	"ADYEN_CLIENT_KEY",
	"ADYEN_API_KEY",
	"ADYEN_API_URL",
	"ADYEN_ENVIRONMENT",
	"GTM_ID",
	"KLAVIYO_COMPANY_ID",
	"KLAVIYO_NEWSLETTER_SIGNUP_URL",
	"STORYBLOK_ADMIN_ACCESS_TOKEN",
	"STORYBLOK_SPACE_ID_V2",
	"STORYBLOK_VARIANT_SYMBOL_ASSET_FOLDER_ID",
	"STORYBLOK_SHIPPING_METHODS_ASSET_FOLDER_ID",
	getStoryBlokTokenName(),
];

/**
 * This array contains the names of all optional environment variables.
 * If it is not set here or as a required variable, it will be ignored when fetching from Azure.
 *
 * @returns {string[]}
 */
export const optionalEnvironmentVariables = () => [
	"SENTRY_FRONTEND_AUTH_TOKEN",
	"SENTRY_REDESIGN_DSN",
	"SENTRY_REDESIGN_SAMPLE_RATE",
	"SENTRY_REDESIGN_TRACES_SAMPLE_RATE",
	"SENTRY_REDESIGN_DEBUG",
	"GTM_ENV_AUTH_STRING",
	"GTM_ENV_PREVIEW_NAME",
	"FORCE_SENTRY",
	"KLEVU_API_KEY",
	"KLEVU_API_URL",
];

/**
 * This array contains pairs of environment variables that should be renamed.
 * Only environment variables that are not already set will be renamed.
 * The first element of each pair is the original name, the second is the new name.
 *
 * If the new name is prefixed with "NEXT_PUBLIC_" it will be available in the client. Only use this prefix for public variables.
 *
 * @returns {[string, string][]}
 */
export const renameEnvironmentVariablesConfig = () => [
	["COMMERCETOOLS_PROJECT_KEY", "GATSBY_COMMERCETOOLS_PROJECT_KEY"],
	["COMMERCETOOLS_API_URL", "GATSBY_COMMERCETOOLS_API_URL"],
	["COMMERCETOOLS_AUTH_URL", "GATSBY_COMMERCETOOLS_AUTH_URL"],
	[
		"COMMERCETOOLS_FRONTEND_CLIENT_ID",
		"GATSBY_COMMERCETOOLS_FRONTEND_CLIENT_ID",
	],
	["COMMERCETOOLS_FRONTEND_SCOPES", "GATSBY_COMMERCETOOLS_FRONTEND_SCOPES"],
	[
		"COMMERCETOOLS_FRONTEND_CLIENT_SECRET",
		"GATSBY_COMMERCETOOLS_FRONTEND_CLIENT_SECRET",
	],
	["BAZAARVOICE_ENV", "GATSBY_BAZAARVOICE_ENV"],
	["MAPBOX_PUBLIC_ACCESS_TOKEN", "GATSBY_MAPBOX_PUBLIC_ACCESS_TOKEN"],
	["ADYEN_CLIENT_KEY", "GATSBY_ADYEN_CLIENT_KEY"],
	["ADYEN_ENVIRONMENT", "GATSBY_ADYEN_ENVIRONMENT"],
	["NETLIFY", "GATSBY_NETLIFY"],
	["BRANCH", "GATSBY_BRANCH"],
	["SENTRY_FRONTEND_AUTH_TOKEN", "SENTRY_AUTH_TOKEN"],
	["SENTRY_REDESIGN_DSN", "GATSBY_SENTRY_REDESIGN_DSN"],
	["FORCE_SENTRY", "GATSBY_FORCE_SENTRY"],
	["SENTRY_REDESIGN_SAMPLE_RATE", "GATSBY_SENTRY_REDESIGN_SAMPLE_RATE"],
	[
		"SENTRY_REDESIGN_TRACES_SAMPLE_RATE",
		"GATSBY_SENTRY_REDESIGN_TRACES_SAMPLE_RATE",
	],
	["SENTRY_REDESIGN_DEBUG", "GATSBY_SENTRY_REDESIGN_DEBUG"],
	[
		"SENTRY_REPLAY_SESSION_SAMPLE_RATE",
		"GATSBY_SENTRY_REPLAY_SESSION_SAMPLE_RATE",
	],
	[
		"SENTRY_REPLAY_ON_ERROR_SAMPLE_RATE",
		"GATSBY_SENTRY_REPLAY_ON_ERROR_SAMPLE_RATE",
	],
	["KLAVIYO_COMPANY_ID", "GATSBY_KLAVIYO_COMPANY_ID"],
	["KLEVU_API_KEY", "GATSBY_KLEVU_API_KEY"],
	["KLEVU_API_URL", "GATSBY_KLEVU_API_URL"],
	[getStoryBlokTokenName(), "GATSBY_STORYBLOK_TOKEN"],
];

export const environmentVariablesToFetch = () => [
	...requiredEnvironmentVariables(),
	...optionalEnvironmentVariables(),
];
